import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Upload, Camera, Send, FileText, MessageSquare, Activity, Download, CheckCircle, Trash2, XCircle, AlertCircle, DollarSign, Building2, CreditCard, User, Store, CalendarDays, Landmark, Hash, StickyNote, Truck, Edit3, Wallet, RotateCcw } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import StatusBadge from '@/components/StatusBadge';
import PaymentTimeline from '@/components/PaymentTimeline';
import AttachmentPreview from '@/components/AttachmentPreview';
import VehicleForm, { saveVehicleLog } from '@/components/VehicleForm';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';
import CameraCapture from '@/components/CameraCapture';
import { FlujoSyncBadge } from '@/components/FlujoSyncBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payment, setPayment] = useState(null);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [vehicleRequired, setVehicleRequired] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(false);
  const [vehicleDraft, setVehicleDraft] = useState(null);
  const [savingVehicle, setSavingVehicle] = useState(false);
  // Adelantos
  const [adelantosDisponibles, setAdelantosDisponibles] = useState([]);
  const [showApplyAdelanto, setShowApplyAdelanto] = useState(false);
  const [applyAdelantoId, setApplyAdelantoId] = useState('');
  const [applyMonto, setApplyMonto] = useState('');
  const [applyingAdelanto, setApplyingAdelanto] = useState(false);
  const fileInputRef = useRef(null);
  const comprobanteInputRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchPaymentDetails();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(() => {
      fetchMessages();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchPaymentDetails = async () => {
    try {
      const [paymentRes, filesRes, messagesRes, logsRes, vehicleRes] = await Promise.allSettled([
        axios.get(`${API}/payments/${id}`),
        axios.get(`${API}/files/payment/${id}`),
        axios.get(`${API}/chat/messages/${id}`),
        axios.get(`${API}/activity/${id}`),
        axios.get(`${API}/payments/${id}/vehiculo`)
      ]);
      
      if (paymentRes.status === 'rejected') {
        const status = paymentRes.reason?.response?.status;
        if (status === 403) {
          toast.error('No tienes permiso para ver este pago');
        } else if (status === 404) {
          toast.error('Pago no encontrado');
        } else {
          toast.error('Error al cargar el pago');
        }
        navigate(-1);
        return;
      }
      
      setPayment(paymentRes.value.data);
      setFiles(filesRes.status === 'fulfilled' ? filesRes.value.data : []);
      setMessages(messagesRes.status === 'fulfilled' ? messagesRes.value.data : []);
      setActivityLogs(logsRes.status === 'fulfilled' ? logsRes.value.data : []);
      if (vehicleRes.status === 'fulfilled') {
        setVehicleRequired(!!vehicleRes.value.data?.requires_vehicle_log);
        setVehicleInfo(vehicleRes.value.data?.vehiculo || null);
      }

      // Cargar adelantos disponibles del creador del pago (si permisos lo permiten)
      try {
        const uid = paymentRes.value.data?.user_id;
        if (uid) {
          const adRes = await axios.get(`${API}/adelantos/beneficiario/${uid}/disponibles`);
          setAdelantosDisponibles(adRes.data || []);
        }
      } catch { /* sin permiso o sin adelantos: silencioso */ }
    } catch (error) {
      toast.error('Error al cargar detalles del pago');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVehicle = async () => {
    setSavingVehicle(true);
    const ok = await saveVehicleLog(id, vehicleDraft);
    setSavingVehicle(false);
    if (ok) {
      toast.success('Bitácora guardada');
      setEditingVehicle(false);
      fetchPaymentDetails();
    }
  };

  const handleApplyAdelanto = async () => {
    if (!applyAdelantoId) { toast.error('Elige un adelanto'); return; }
    const monto = parseFloat(applyMonto);
    if (!monto || monto <= 0) { toast.error('Monto inválido'); return; }
    setApplyingAdelanto(true);
    try {
      await axios.post(`${API}/adelantos/${applyAdelantoId}/apply`, {
        payment_id: id,
        monto_aplicado: monto,
      });
      toast.success('Adelanto aplicado al pago');
      setShowApplyAdelanto(false);
      setApplyAdelantoId(''); setApplyMonto('');
      fetchPaymentDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aplicar el adelanto');
    } finally {
      setApplyingAdelanto(false);
    }
  };

  const handleRevertAdelanto = async (v) => {
    const motivo = window.prompt(`¿Revertir Q${Number(v.monto).toFixed(2)}? El monto regresará al saldo del adelanto.\nMotivo (opcional):`, '');
    if (motivo === null) return;
    try {
      await axios.post(`${API}/adelantos/${v.adelanto_id}/revert`, { payment_id: id, applied_at: v.applied_at, motivo });
      toast.success('Adelanto revertido');
      fetchPaymentDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo revertir el adelanto');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/messages/${id}`);
      setMessages(response.data);
      // Mark messages as read when viewing the chat
      markMessagesAsRead();
    } catch (error) {
      console.error('Error al cargar mensajes');
    }
  };

  const markMessagesAsRead = async () => {
    try {
      await axios.put(`${API}/chat/mark-read/${id}`);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('payment_id', id);

    try {
      await axios.post(`${API}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Archivo subido exitosamente');
      fetchPaymentDetails();
    } catch (error) {
      toast.error('Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleCameraCapture = async (blob) => {
    const file = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
    await handleFileUpload(file);
    setShowCamera(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API}/chat/messages`, {
        payment_id: id,
        message: newMessage,
        file_ids: []
      });
      setNewMessage('');
      fetchMessages();
      fetchPaymentDetails();
    } catch (error) {
      toast.error('Error al enviar mensaje');
    }
  };

  const handleApprovePayment = async () => {
    try {
      let comprobante_file_id = null;
      
      // Upload comprobante if provided (optional for cash payments)
      if (comprobanteFile) {
        const formData = new FormData();
        formData.append('file', comprobanteFile);
        formData.append('payment_id', id);
        
        const uploadRes = await axios.post(`${API}/files/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        comprobante_file_id = uploadRes.data.id;
      }

      // Approve payment (with or without comprobante)
      await axios.post(`${API}/payments/approve`, {
        payment_id: id,
        comprobante_file_id: comprobante_file_id
      });

      if (!comprobanteFile) {
        toast.success('Pago aprobado exitosamente (sin comprobante)');
      } else {
        toast.success('Pago aprobado exitosamente');
      }
      
      setShowApproveDialog(false);
      setComprobanteFile(null);
      fetchPaymentDetails();
    } catch (error) {
      toast.error('Error al aprobar el pago');
    }
  };

  const handleRejectPayment = async () => {
    if (!rejectReason.trim()) {
      toast.error('Debes proporcionar un motivo para rechazar el pago');
      return;
    }

    try {
      await axios.post(`${API}/payments/reject`, {
        payment_id: id,
        motivo: rejectReason
      });

      toast.success('Pago rechazado exitosamente');
      setShowRejectDialog(false);
      setRejectReason('');
      fetchPaymentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar el pago');
    }
  };

  const handleDeletePayment = async () => {
    if (!window.confirm('¿Estás seguro de eliminar este pago? Esta acción no se puede deshacer y eliminará todos los archivos, mensajes y registros asociados.')) {
      return;
    }

    try {
      await axios.delete(`${API}/payments/${id}`);
      toast.success('Pago eliminado exitosamente');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el pago');
    }
  };

  const handleMacrobaseToggle = async () => {
    try {
      const newStatus = !payment.ingresada_macrobase;
      await axios.post(`${API}/payments/macrobase`, {
        payment_id: id,
        ingresada: newStatus
      });
      toast.success(newStatus ? '✅ Marcada como ingresada a ERP' : 'Desmarcada de ERP');
      fetchPaymentDetails();
    } catch (error) {
      toast.error('Error al actualizar estado de ERP');
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      // First, check if file has a public URL (from GCS)
      const fileData = files.find(f => f.id === fileId);
      
      if (fileData?.file_url && fileData.file_url.startsWith('http')) {
        // If it's a public URL from GCS, use it directly
        const link = document.createElement('a');
        link.href = fileData.file_url;
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Archivo descargado exitosamente');
        return;
      }
      
      // Otherwise, download through backend
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/files/${fileId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Archivo descargado exitosamente');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Error al descargar archivo. Por favor intenta nuevamente.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-lg font-medium text-slate-600 dark:text-slate-400">Cargando...</div>
      </div>
    );
  }

  // Handle case where payment is null (e.g., 403 error already navigated away)
  if (!payment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-lg font-medium text-slate-600 dark:text-slate-400">Pago no disponible</div>
      </div>
    );
  }

  const isPaid = ['pagada', 'aprobada'].includes(payment.status);
  const isRejected = payment.status === 'rechazada';
  const canApproveReject = ['admin', 'gerencia', 'supervisor', 'encargada_compras', 'asistente_admin'].includes(user?.role) && payment.status === 'pendiente';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="payment-detail-page">
      <div className="container mx-auto px-4 py-6 lg:py-8">
        {/* Top bar: back + actions */}
        <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800"
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>

          <div className="flex flex-wrap gap-2">
            {canApproveReject && (
              <>
                <Button
                  onClick={() => setShowApproveDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                  data-testid="approve-payment-button"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Aprobar
                </Button>
                <Button
                  onClick={() => setShowRejectDialog(true)}
                  variant="outline"
                  className="border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                  data-testid="reject-payment-button"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Rechazar
                </Button>
              </>
            )}
            {user?.role === 'admin' && (
              <Button
                onClick={handleDeletePayment}
                variant="destructive"
                data-testid="delete-payment-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* HERO / Header card */}
        <Card className={`relative overflow-hidden p-6 mb-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 ${isPaid ? 'ring-1 ring-emerald-200 dark:ring-emerald-900/60' : isRejected ? 'ring-1 ring-red-200 dark:ring-red-900/60' : ''}`}>
          <div className={`absolute inset-x-0 top-0 h-1 ${isPaid ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' : isRejected ? 'bg-gradient-to-r from-red-400 to-red-600' : 'bg-gradient-to-r from-amber-400 to-amber-500'}`} />
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <StatusBadge status={payment.status} data-testid="payment-detail-status" />
                {payment.tipo_pago === 'credito' && (
                  <Badge variant="outline" className="text-[10px] rounded-full bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60">Crédito</Badge>
                )}
                {payment.is_overdue && payment.status === 'pendiente' && (
                  <Badge variant="outline" className="text-[10px] rounded-full bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/60">
                    Vencido {payment.days_overdue > 0 ? `hace ${payment.days_overdue}d` : ''}
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 dark:text-slate-100 truncate" data-testid="payment-detail-title">
                {payment.proveedor_nombre || payment.proveedor}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                NIT {payment.nit_proveedor} · Serie {payment.serie || '—'} · DT {payment.numero_dt || '—'}
              </p>
            </div>

            <div className="lg:text-right shrink-0">
              <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">Monto a depositar</p>
              <p className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-slate-100 tabular-nums" data-testid="payment-amount">
                Q{payment.monto_depositar.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5 lg:text-right">
                Forma de pago: <span className="font-medium text-slate-700 dark:text-slate-300 capitalize">{payment.forma_pago}</span>
              </p>
            </div>
          </div>
        </Card>

        {/* Previous rejection warning */}
        {payment.previous_rejection_id && payment.status !== 'rechazada' && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-900/60 rounded-lg" data-testid="previous-rejection-alert">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900 dark:text-amber-200">
                  Factura re-enviada tras rechazo previo
                  {payment.resubmission_count > 1 && ` (intento #${payment.resubmission_count + 1})`}
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                  <strong>Motivo del rechazo anterior:</strong> {payment.previous_rejection_reason || 'Sin motivo registrado'}
                </p>
                {payment.previous_rejection_date && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Rechazado previamente el {format(new Date(payment.previous_rejection_date), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                  </p>
                )}
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-2 italic">
                  Revisar si el problema fue corregido antes de aprobar.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Info */}
        {payment.status === 'rechazada' && payment.motivo_rechazo && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/60 rounded-lg">
            <div className="flex items-start space-x-3">
              <XCircle className="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-300">Pago Rechazado</p>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1"><strong>Motivo:</strong> {payment.motivo_rechazo}</p>
                {payment.fecha_rechazo && (
                  <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                    Rechazado el {format(new Date(payment.fecha_rechazo), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Two-column layout: MAIN + ASIDE */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          {/* MAIN */}
          <div className="min-w-0 space-y-6">
            {/* Payment details grid */}
            <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">Datos del pago</h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <DetailField icon={User} label="Enviado por" value={payment.nombre_envia} />
                {payment.forma_pago === 'efectivo' ? (
                  <DetailField icon={Hash} label="Número de bauche (efectivo)" value={payment.numero_bauche || 'No especificado'} />
                ) : (
                  <>
                    <DetailField icon={Landmark} label="Banco" value={payment.banco} />
                    <DetailField icon={CreditCard} label="Cuenta" value={`${payment.numero_cuenta} (${payment.tipo_cuenta})`} />
                    <DetailField icon={Building2} label="Titular de la cuenta" value={payment.titular_cuenta || 'No especificado'} />
                  </>
                )}
                <DetailField icon={Store} label="Tienda / Centro de costo" value={payment.tienda_nombre || payment.centro_costo || '—'} />
                <DetailField icon={DollarSign} label="Tipo de pago" value={payment.tipo_pago === 'credito' ? 'Crédito' : 'Contado'} />
                {payment.fecha_pago_programada && (
                  <DetailField icon={CalendarDays} label="Pago programado" value={format(new Date(payment.fecha_pago_programada), 'dd MMM yyyy', { locale: es })} />
                )}
                <DetailField icon={Hash} label="Serie / DT" value={`${payment.serie || '—'} · ${payment.numero_dt || '—'}`} />
              </dl>
              {payment.datos_factura && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-start gap-2">
                    <StickyNote className="h-4 w-4 text-slate-400 dark:text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Datos de factura</p>
                      <p className="text-sm text-slate-800 dark:text-slate-200 mt-1 whitespace-pre-line">{payment.datos_factura}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Macrobase Checkbox */}
              {((['pagada', 'aprobada'].includes(payment.status) || payment.tipo_pago === 'credito') && ['admin', 'gerencia', 'supervisor', 'encargada_compras', 'asistente_admin'].includes(user?.role)) && (
                <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <label htmlFor="macrobase-checkbox" className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        id="macrobase-checkbox"
                        checked={payment.ingresada_macrobase || false}
                        onChange={handleMacrobaseToggle}
                        className="w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Factura ingresada a ERP
                        {payment.tipo_pago === 'credito' && payment.status === 'pendiente' && (
                          <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">(Crédito - Pendiente de pago)</span>
                        )}
                      </span>
                    </label>
                    {payment.ingresada_macrobase && payment.ingresada_macrobase_por && (
                      <span className="text-xs text-slate-500 dark:text-slate-500">
                        ✓ Marcada por {payment.ingresada_macrobase_por}
                        {payment.ingresada_macrobase_fecha && (
                          <> el {format(new Date(payment.ingresada_macrobase_fecha), 'dd/MM/yyyy HH:mm', { locale: es })}</>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </Card>

            <FlujoSyncBadge origenId={id} resyncable={payment.status === 'pagada'} />
            {/* Adelantos del beneficiario (Bolsón) */}
            {(() => {
              const totalAplicado = Number(payment.adelanto_aplicado || 0);
              const saldoPago = Math.max(0, Number(payment.monto_depositar || 0) - totalAplicado);
              const hasPermApply = (user?.role === 'admin') || (user?.permissions || []).includes('aplicar_adelanto');
              const showCard = totalAplicado > 0 || (adelantosDisponibles.length > 0 && hasPermApply && saldoPago > 0);
              if (!showCard) return null;
              return (
                <Card className="p-5 border-emerald-200 dark:border-emerald-900/60 bg-gradient-to-br from-emerald-50/60 via-white to-white dark:from-emerald-950/20 dark:via-slate-900 dark:to-slate-900" data-testid="adelantos-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-950/60 dark:to-emerald-900/40 flex items-center justify-center shrink-0">
                        <Wallet className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                          {totalAplicado > 0 ? (payment.cubierto_con_adelanto ? 'Cubierto con adelanto' : 'Parcialmente cubierto con adelanto') : 'Adelanto disponible del beneficiario'}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {totalAplicado > 0
                            ? `Se aplicaron Q${totalAplicado.toLocaleString('es-GT',{minimumFractionDigits:2})} del bolsón. Saldo pago restante: Q${saldoPago.toLocaleString('es-GT',{minimumFractionDigits:2})}`
                            : `Hay ${adelantosDisponibles.length} adelanto(s) con saldo del usuario que creó este pago.`}
                        </p>
                      </div>
                    </div>
                    {hasPermApply && saldoPago > 0 && adelantosDisponibles.length > 0 && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setApplyAdelantoId(adelantosDisponibles[0]?.id || '');
                          const maxMonto = Math.min(adelantosDisponibles[0]?.monto_disponible || 0, saldoPago);
                          setApplyMonto(maxMonto.toFixed(2));
                          setShowApplyAdelanto(true);
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="apply-adelanto-btn"
                      >
                        <Wallet className="h-4 w-4 mr-2" /> Aplicar adelanto
                      </Button>
                    )}
                  </div>
                  {/* Lista de adelantos vinculados */}
                  {(payment.adelantos_vinculados || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {(payment.adelantos_vinculados || []).map((v, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs bg-white dark:bg-slate-900 rounded-md p-2 border border-emerald-100 dark:border-emerald-900/40">
                          <button
                            className="text-[#2c57c5] hover:underline"
                            onClick={() => navigate(`/adelantos/${v.adelanto_id}`)}
                            data-testid={`linked-adelanto-${idx}`}
                          >
                            {v.beneficiario_nombre
                              ? `Adelanto de ${v.beneficiario_nombre}${v.beneficiario_id && v.beneficiario_id !== payment.user_id ? ' — cuenta vinculada' : ''}`
                              : `Adelanto ${String(v.adelanto_id).slice(0, 8)}…`}
                          </button>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-emerald-700 dark:text-emerald-300 tabular-nums">
                              Q{Number(v.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                            </span>
                            {((user?.role === 'admin') || (user?.permissions || []).includes('revertir_adelanto')) && (
                              <button
                                className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 font-medium"
                                onClick={() => handleRevertAdelanto(v)}
                                data-testid={`revert-adelanto-${idx}`}
                                title="Revertir: el monto regresa al saldo del adelanto"
                              >
                                <RotateCcw className="h-3.5 w-3.5" /> Revertir
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })()}

            {/* Bitácora del vehículo (solo para proveedores de combustible) */}
            {vehicleRequired && (
              <Card className="p-6 border-amber-200 dark:border-amber-900/60 bg-gradient-to-br from-amber-50/60 via-white to-white dark:from-amber-950/20 dark:via-slate-900 dark:to-slate-900" data-testid="vehicle-log-card">
                <div className="flex items-start justify-between mb-4 gap-2 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-950/60 dark:to-amber-900/40 flex items-center justify-center shrink-0">
                      <Truck className="h-5 w-5 text-amber-700 dark:text-amber-300" />
                    </div>
                    <div>
                      <h3 className="text-lg font-display font-semibold text-slate-900 dark:text-slate-100">Bitácora del vehículo</h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Combustible universal - datos del viaje</p>
                    </div>
                  </div>
                  {!editingVehicle && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setVehicleDraft(vehicleInfo || {
                        destino_origen: '', destino_destino: '', placas: '', conductor: '',
                        km_inicial: null, km_final: null, tanque_restante_litros: null,
                        danos_reportados: false, danos_descripcion: '', danos_fotos_ids: [],
                        pasajeros_reportados: false, pasajeros: [],
                      }); setEditingVehicle(true); }}
                      className="border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40"
                      data-testid="edit-vehicle-button"
                    >
                      <Edit3 className="h-3.5 w-3.5 mr-1" />
                      {vehicleInfo ? 'Editar' : 'Registrar'}
                    </Button>
                  )}
                </div>

                {editingVehicle ? (
                  <div className="space-y-3" data-testid="vehicle-edit-mode">
                    <VehicleForm
                      value={vehicleDraft}
                      onChange={setVehicleDraft}
                      tiendaId={payment.tienda_id}
                      paymentId={id}
                      files={files}
                      onFilesRefresh={fetchPaymentDetails}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => { setEditingVehicle(false); setVehicleDraft(null); }}
                        disabled={savingVehicle}
                        data-testid="vehicle-cancel-button"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSaveVehicle}
                        disabled={savingVehicle}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="vehicle-save-button"
                      >
                        {savingVehicle ? 'Guardando...' : 'Guardar bitácora'}
                      </Button>
                    </div>
                  </div>
                ) : vehicleInfo ? (
                  <VehicleReadView vehicleInfo={vehicleInfo} files={files} />
                ) : (
                  <div className="text-center py-6 text-sm text-amber-800 dark:text-amber-300" data-testid="vehicle-empty">
                    <AlertCircle className="h-6 w-6 mx-auto mb-2 text-amber-500" />
                    Aún no se ha registrado la bitácora del viaje.
                  </div>
                )}
              </Card>
            )}

            {/* Tabs: Archivos + Chat */}
            <Tabs defaultValue="files" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="tabs-list">
                <TabsTrigger value="files" data-testid="files-tab" className="dark:data-[state=active]:bg-slate-800">Archivos ({files.length})</TabsTrigger>
                <TabsTrigger value="chat" data-testid="chat-tab" className="dark:data-[state=active]:bg-slate-800">Chat ({messages.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="files" data-testid="files-content">
                <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                  <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Archivos adjuntos</h2>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => setShowCamera(true)}
                        variant="outline"
                        disabled={uploading}
                        data-testid="open-camera-button"
                      >
                        <Camera className="mr-2 h-4 w-4" />
                        Cámara
                      </Button>
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white"
                        data-testid="upload-file-button"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {uploading ? 'Subiendo...' : 'Subir archivo'}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="file-input"
                      />
                    </div>
                  </div>

                  {files.length === 0 ? (
                    <div className="text-center py-12" data-testid="no-files-message">
                      <FileText className="h-12 w-12 text-slate-400 dark:text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-600 dark:text-slate-400">No hay archivos adjuntos</p>
                    </div>
                  ) : (
                    <AttachmentPreview
                      files={files}
                      comprobanteId={payment.comprobante_pago_id}
                      onDownload={handleDownloadFile}
                    />
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="chat" data-testid="chat-content">
                <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4">Chat</h2>

                  <div className="space-y-4 mb-4 max-h-96 overflow-y-auto" data-testid="chat-messages">
                    {messages.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="h-12 w-12 text-slate-400 dark:text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-600 dark:text-slate-400">No hay mensajes aún</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.user_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          data-testid={`chat-message-${msg.id}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                              msg.user_id === user?.id
                                ? 'bg-[#2c57c5] text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100'
                            }`}
                          >
                            <p className="text-xs font-medium mb-1 opacity-80">{msg.user_name}</p>
                            <p className="text-sm whitespace-pre-line">{msg.message}</p>
                            <p className={`text-[10px] mt-1 ${
                              msg.user_id === user?.id ? 'text-blue-200' : 'text-slate-500 dark:text-slate-500'
                            }`}>
                              {format(new Date(msg.created_at), 'HH:mm', { locale: es })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  <div className="flex space-x-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Escribe un mensaje..."
                      data-testid="chat-input"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white"
                      data-testid="send-message-button"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* ASIDE (Timeline) */}
          <aside className="min-w-0">
            <div className="lg:sticky lg:top-20 space-y-6">
              <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="timeline-card">
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200/70 dark:from-blue-950/60 dark:to-blue-900/40 flex items-center justify-center">
                    <Activity className="h-4 w-4 text-[#2c57c5] dark:text-blue-300" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Línea de tiempo</h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-500">Historial del pago</p>
                  </div>
                </div>
                <PaymentTimeline logs={activityLogs} />
              </Card>
            </div>
          </aside>
        </div>
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <div key="camera-modal">
          <CameraCapture
            onCapture={handleCameraCapture}
            onClose={() => setShowCamera(false)}
          />
        </div>
      )}

      {/* Approve Payment Dialog */}
      {showApproveDialog && (
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Aprobar Pago</DialogTitle>
              <DialogDescription>
                Sube el comprobante de pago (opcional para pagos en efectivo).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Comprobante de Pago (Opcional)</Label>
                <Input
                  ref={comprobanteInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setComprobanteFile(e.target.files[0])}
                  data-testid="comprobante-input"
                />
                {comprobanteFile ? (
                  <p className="text-sm text-slate-600 dark:text-slate-400">Archivo: {comprobanteFile.name}</p>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-500">Para pagos en efectivo, puedes aprobar sin comprobante</p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleApprovePayment}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="confirm-approve-button"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Aprobar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reject Payment Dialog */}
      {showRejectDialog && (
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 dark:text-red-400">Rechazar Pago</DialogTitle>
              <DialogDescription>
                Proporciona un motivo para rechazar esta solicitud de pago.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Motivo del Rechazo *</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ej: Datos incorrectos, boleta ilegible, monto incorrecto..."
                  rows={4}
                  data-testid="reject-reason-input"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectReason(''); }}>
                Cancelar
              </Button>
              <Button 
                onClick={handleRejectPayment}
                disabled={!rejectReason.trim()}
                variant="destructive"
                data-testid="confirm-reject-button"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Rechazar Pago
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Apply Adelanto Dialog */}
      {showApplyAdelanto && (
        <Dialog open={showApplyAdelanto} onOpenChange={(v) => { if (!v) setShowApplyAdelanto(false); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-emerald-600" /> Aplicar adelanto al pago
              </DialogTitle>
              <DialogDescription>
                Descuenta un monto del bolsón del beneficiario y vincúlalo a este pago. No se realiza transferencia adicional.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Adelanto a aplicar</Label>
                <select
                  value={applyAdelantoId}
                  onChange={(e) => {
                    setApplyAdelantoId(e.target.value);
                    const sel = adelantosDisponibles.find(a => a.id === e.target.value);
                    const saldoPago = Math.max(0, Number(payment.monto_depositar || 0) - Number(payment.adelanto_aplicado || 0));
                    if (sel) setApplyMonto(Math.min(sel.monto_disponible, saldoPago).toFixed(2));
                  }}
                  className="w-full mt-1 border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                  data-testid="apply-adelanto-select"
                >
                  {adelantosDisponibles.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.beneficiario_nombre}{a.beneficiario_id !== payment.user_id ? ' (cuenta vinculada)' : ''} · {a.banco} · Saldo Q{Number(a.monto_disponible).toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Monto a aplicar (Q)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={applyMonto}
                  onChange={(e) => setApplyMonto(e.target.value)}
                  data-testid="apply-monto-input"
                />
                {applyAdelantoId && (() => {
                  const sel = adelantosDisponibles.find(a => a.id === applyAdelantoId);
                  const saldoPago = Math.max(0, Number(payment.monto_depositar || 0) - Number(payment.adelanto_aplicado || 0));
                  const maxAplicar = sel ? Math.min(sel.monto_disponible, saldoPago) : 0;
                  return (
                    <p className="text-[11px] text-slate-500 mt-1">Máximo aplicable: Q{maxAplicar.toFixed(2)}</p>
                  );
                })()}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowApplyAdelanto(false)} disabled={applyingAdelanto}>Cancelar</Button>
              <Button onClick={handleApplyAdelanto} disabled={applyingAdelanto} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-apply-adelanto">
                {applyingAdelanto ? 'Aplicando...' : 'Aplicar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Small helper component for detail fields
function DetailField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="h-7 w-7 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
          <Icon className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <dt className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</dt>
        <dd className="text-sm text-slate-900 dark:text-slate-100 truncate mt-0.5">{value}</dd>
      </div>
    </div>
  );
}

// Vista de solo lectura de la bitácora del vehículo
function VehicleReadView({ vehicleInfo, files }) {
  const v = vehicleInfo || {};
  const ini = v.km_inicial;
  const fin = v.km_final;
  const recorridos = (typeof ini === 'number' && typeof fin === 'number') ? fin - ini : null;
  const damagePhotos = (v.danos_fotos_ids || [])
    .map(fid => files.find(f => f.id === fid))
    .filter(Boolean);

  return (
    <div className="space-y-4" data-testid="vehicle-read-view">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <DetailField label="Origen" value={v.destino_origen || '—'} />
        <DetailField label="Destino" value={v.destino_destino || '—'} />
        <DetailField label="Placas" value={v.placas || '—'} />
        <DetailField label="Conductor" value={v.conductor || '—'} />
        <DetailField label="Km inicial" value={ini !== null && ini !== undefined ? `${ini} km` : '—'} />
        <DetailField label="Km final" value={fin !== null && fin !== undefined ? `${fin} km` : 'Pendiente'} />
        <DetailField label="Km recorridos" value={recorridos !== null ? `${recorridos.toFixed(0)} km` : '—'} />
        <DetailField label="Tanque restante" value={v.tanque_restante_litros !== null && v.tanque_restante_litros !== undefined ? `${v.tanque_restante_litros}` : '—'} />
      </dl>

      {/* Daños */}
      <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Daños reportados</p>
        {v.danos_reportados ? (
          <div className="rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/60 p-3 space-y-2">
            <p className="text-sm text-red-800 dark:text-red-300 whitespace-pre-line">{v.danos_descripcion || 'Sin descripción'}</p>
            {damagePhotos.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {damagePhotos.map(f => (
                  <Badge key={f.id} variant="outline" className="bg-white dark:bg-slate-900 border-red-300 dark:border-red-900/60 text-red-700 dark:text-red-300 text-[10px]">
                    <FileText className="h-3 w-3 mr-1" /> {f.original_filename}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Sin daños reportados</p>
        )}
      </div>

      {/* Pasajeros */}
      <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Pasajeros</p>
        {v.pasajeros_reportados && (v.pasajeros || []).length > 0 ? (
          <ul className="space-y-1.5">
            {v.pasajeros.map((p, i) => (
              <li key={i} className="text-sm text-slate-800 dark:text-slate-200 flex items-center gap-2" data-testid={`vehicle-passenger-${i}`}>
                <User className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                <span className="font-medium">{p.nombre}</span>
                {p.motivo && <span className="text-slate-500 dark:text-slate-400">· {p.motivo}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-400">Sin pasajeros</p>
        )}
      </div>

      {v.updated_by_name && (
        <p className="text-[11px] text-slate-400 dark:text-slate-600 italic mt-2">
          Última actualización por {v.updated_by_name}
        </p>
      )}
    </div>
  );
}
